import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableSkeleton, TopProgress } from '../../../../../../components'
import {
  Container,
  TopInfoWrapper
} from './style'
import {timeConverterMMDDYYHHMM} from '../../../../../../utils/dates'
import { utilsService } from '../../../../../../api'
import { useQueryParam, NumberParam, StringParam } from 'use-query-params'
import { StateWrapper, GreenDot, Greentext } from '../../../../../cloud/Instances/styles'

type ActivityLogRecord = {
  id:number
  created_at:string
  state?:string | null
  type?:string | null
  log:string
  details?:string | null
}

const ActivityLog:FC<any> = ({user}) => {
  const [t] = useTranslation()
  const [page] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering] = useQueryParam('ordering', StringParam)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [logs, setLogs] = useState<ActivityLogRecord[]>([])
  const [paginationData, setPaginationData] = useState<any>()

  useEffect(() => {
    const currentPage = page || 1
    const currentPageSize = pageSize || 10
    const currentOrdering = ordering || '-created_at'
    const userId = user?.id || user?.user?.id

    if(!userId) return

    setPageLoading(true)
    utilsService.getActivityLog({
      page: currentPage,
      page_size: currentPageSize,
      ordering: currentOrdering,
      filtering: `user:${userId}`,
    })
    .then((res) => {
      setLogs(res.data?.objects || [])
      setPaginationData(res.data)
    })
    .catch(() => {
      setLogs([])
      setPaginationData(undefined)
    })
    .finally(() => setPageLoading(false))
  },[page, pageSize, ordering, user])

  const columns = useMemo(() => ([
    {title:t('DATE'), key:'created_at'},
    {title:t('STATUS'), key:'state'},
    {title:t('TYPE'), key:'type'},
    {title:t('LOG'), key:'log'},
  ]),[t])

  const rows = useMemo(() => logs.map((item) => {
    const stateValue = item.state || item.type || ''
    return {
      ...item,
      state: (
        <StateWrapper>
          <GreenDot active={stateValue || 'unknown'}/>
          <Greentext active={stateValue || 'unknown'} style={{textTransform:'capitalize'}}>
            {stateValue || t('NOT_AVAILABLE')}
          </Greentext>
        </StateWrapper>
      ),
      type: item.type || t('NOT_AVAILABLE'),
      created_at: item.created_at ? timeConverterMMDDYYHHMM(item.created_at) : t('NOT_AVAILABLE'),
      log: (
        <div>
          <div>{item.log || t('NOT_AVAILABLE')}</div>
          {item.details ? <div style={{opacity:0.7, marginTop:4}}>{item.details}</div> : null}
        </div>
      )
    }
  }),[logs, t])

  return(
    <Container>
      <TopProgress loading={pageLoading}/>
      <TopInfoWrapper>
        {pageLoading ? (
          <TableSkeleton rows={5} columns={columns.length} columnsData={columns}/>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            hideCheckbox
            paginationData={paginationData}
            data-test-id="user-activity-log-table"
          />
        )}
      </TopInfoWrapper>
    </Container>
  )
}
export default ActivityLog
